import { Button } from '@/components/ui/button';
import { UserPlus, UserMinus, Ban, Shield, Settings, Link2, Crown, Users } from 'lucide-react';
import type { GuildActivityEntry } from '@/lib/api';

interface GuildActivityFeedProps {
  activities: GuildActivityEntry[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

const activityIcons: Record<string, typeof UserPlus> = {
  member_joined: UserPlus,
  member_left: UserMinus,
  member_kicked: UserMinus,
  member_banned: Ban,
  member_unbanned: Users,
  role_changed: Shield,
  guild_updated: Settings,
  invite_created: Link2,
  invite_used: Link2,
  guild_created: Users,
  ownership_transferred: Crown,
};

const activityLabels: Record<string, string> = {
  member_joined: 'joined the guild',
  member_left: 'left the guild',
  member_kicked: 'was kicked from the guild',
  member_banned: 'was banned',
  member_unbanned: 'was unbanned',
  role_changed: 'had their role changed',
  guild_updated: 'updated guild settings',
  invite_created: 'created an invite',
  invite_used: 'used an invite',
  guild_created: 'created the guild',
  ownership_transferred: 'transferred ownership',
};

export function GuildActivityFeed({
  activities,
  loading,
  hasMore,
  onLoadMore,
}: GuildActivityFeedProps) {
  if (loading && activities.length === 0) {
    return <p className="text-sm text-muted-foreground">Loading activity...</p>;
  }

  if (activities.length === 0) {
    return <p className="text-sm text-muted-foreground">No activity yet.</p>;
  }

  return (
    <div className="space-y-3">
      {activities.map((activity) => {
        const Icon = activityIcons[activity.activityType] || Users;
        const label = activityLabels[activity.activityType] || activity.activityType;

        return (
          <div
            key={activity.activityId}
            className="flex items-start gap-3 py-2 border-b last:border-0"
          >
            <div className="mt-0.5 text-muted-foreground">
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm">
                <span className="font-mono text-xs">{activity.username || 'Unknown'}</span> {label}
              </p>
              <p className="text-xs text-muted-foreground">
                {new Date(activity.createdAt).toLocaleString()}
              </p>
            </div>
          </div>
        );
      })}
      {hasMore && (
        <Button variant="ghost" size="sm" onClick={onLoadMore} className="w-full">
          Load more
        </Button>
      )}
    </div>
  );
}
